import React, { useState, useEffect } from 'react';
import { Button, message, Table, Tag } from 'antd';
import { useSelector } from 'react-redux';
import api from '../functions/api';
import getUserId from '../functions/getUserId';

const RebalanceScenarios = () => {
    const [scenarios, setScenarios] = useState([]);
    const [scenarioResults, setScenarioResults] = useState([]);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [llmAnalysis, setLlmAnalysis] = useState(null);   // DAS-2593
    const [llmLoading, setLlmLoading] = useState(false);    // DAS-2593
    const [etfCandidates, setEtfCandidates] = useState([]); // DAS-2594

    const parametersData = useSelector(state => state.portfolio.parametersData.DP_dataset_json);

    // DAS-2592: regime-aware suggestion (suggest-only — the user still picks).
    // Reuses the current market regime already in Redux (state.portfolio.data.regime.current).
    const currentRegime = useSelector(state => state?.portfolio?.data?.regime?.current ?? null);
    const REGIME_SUGGESTION = {
        BEAR: 'Market Crash or Economic Downturn',
        BULL: 'Profit-Taking (Greedy or Tactical)',
        NEUTRAL: 'Portfolio Rebalancing (Strategic)',
    };
    const suggestedScenario = currentRegime ? REGIME_SUGGESTION[currentRegime.toUpperCase()] : null;

    useEffect(() => {
        const fetchScenarios = async () => {
            try {
                const response = await api.get('/rebalance_scenarios');
                if (response.status === 200) {
                    const scenarios = response.data;
                    console.log('Scenarios:', scenarios);
                    setScenarios(scenarios);
                } else {
                    message.error('Failed to retrieve scenarios');
                }
            } catch (error) {
                message.error('Failed to retrieve scenarios');
            }
        };

        fetchScenarios();

        // DAS-2594: load the thin ETF/fund review (portfolio-wide, no scenario needed).
        const fetchEtfCandidates = async () => {
            try {
                const response = await api.get('/rebalance_etf_candidates', {
                    params: { userid: getUserId() }
                });
                if (response.status === 200 && Array.isArray(response.data)) {
                    setEtfCandidates(response.data);
                }
            } catch (error) {
                // non-fatal: ETF review is supplementary
            }
        };
        fetchEtfCandidates();
    }, []);

    const handleScenarioSelect = async (scenario) => {
        console.log('Selected Scenario:', scenario);
        setSelectedScenario(scenario);
        setLlmAnalysis(null);   // DAS-2593: clear stale AI analysis on scenario change

        const userId = getUserId();
        try {
            const response = await api.get('/rebalance_scenarios', {
                params: { userid: userId, scenario }
            });
            if (response.status === 200) {
                console.log('Scenario Results:', response.data);
                setScenarioResults(response.data);
                message.success('Scenario results successfully retrieved');
            } else {
                message.error('Failed to retrieve scenario results');
            }
        } catch (error) {
            message.error('Failed to retrieve scenario results');
        }
    };

    // DAS-2593: on-demand LLM curation/narrative for the selected scenario.
    const handleAiAnalysis = async () => {
        if (!selectedScenario) return;
        setLlmLoading(true);
        try {
            const response = await api.get('/rebalance_scenario_llm', {
                params: { userid: getUserId(), scenario: selectedScenario }
            });
            if (response.status === 200) {
                setLlmAnalysis(response.data);
                if (response.data?.error) {
                    message.warning('AI analysis returned with a parsing issue — showing raw output.');
                } else {
                    message.success('AI analysis ready');
                }
            } else {
                message.error('Failed to run AI analysis');
            }
        } catch (error) {
            message.error('Failed to run AI analysis');
        } finally {
            setLlmLoading(false);
        }
    };

    const formatValue = (metric) => {
        const parameter = parametersData.find(param => param.DataField === metric.metric);
        const showInfront = parameter?.show_infront || '';
        const showBehind = parameter?.show_behind || '';
        return `${showInfront}${metric.value}${showBehind}`;
    };

    const columns = [
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            key: 'ticker',
            render: (text, record) => (
                <div>
                    <Tag color="blue">{record?.ticker || 'N/A'}</Tag>
                    <div>{record.company}</div>
                    <div>Total Score: {parseInt(record.total_score)}</div>
                    {record.fundamental_score != null && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            Fundamentals {parseInt(record.fundamental_score)} + concentration {record.concentration_bonus}
                            {record.concentration_pct != null && ` (${record.concentration_pct}% of equity)`}
                            {record.gain_bonus ? ` + gain ${record.gain_bonus}` : ''}
                        </div>
                    )}
                    {record.fundamental_score == null && record.basis === 'concentration' && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            Concentration trim candidate: {record.concentration_pct}% of equity portfolio
                        </div>
                    )}
                    {record.fundamental_score == null && record.basis === 'sell_ease' && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            Sell-ease (raise cash): {record.concentration_pct}% of equity, P/L {record.unrealized_pl_pct}%
                            {record.tax_ease_bonus != null && ` (tax tilt ${record.tax_ease_bonus})`}
                        </div>
                    )}
                    {record.unrealized_pl_pct != null && (
                        <div style={{ fontSize: '12px', color: record.unrealized_pl_pct < 0 ? '#cf1322' : '#389e0d' }}>
                            Unrealized P/L: {record.unrealized_pl_eur != null ? `€${record.unrealized_pl_eur} ` : ''}({record.unrealized_pl_pct}%)
                        </div>
                    )}
                    <div>Total Amount Exposure: €{record.total_amount_exposure}</div>
                </div>
            ),
        },
        {
            title: 'Metric',
            dataIndex: 'metrics',
            key: 'metric',
            render: (metrics) => (
                <div>
                    {metrics.map(metric => (
                        <div key={metric.metric}>{metric.metric}</div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Value',
            dataIndex: 'metrics',
            key: 'value',
            render: (metrics) => (
                <div>
                    {metrics.map(metric => (
                        <div key={metric.metric}>{formatValue(metric)}</div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Sector Avg',
            dataIndex: 'metrics',
            key: 'sector_avg',
            render: (metrics) => (
                <div>
                    {metrics.map(metric => (
                        <div key={metric.metric}>{metric.sector_avg}</div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Weight',
            dataIndex: 'metrics',
            key: 'weight',
            render: (metrics) => (
                <div>
                    {metrics.map(metric => (
                        <div key={metric.metric}>{parseInt(metric.weight)}%</div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Score',
            dataIndex: 'metrics',
            key: 'score',
            render: (metrics) => (
                <div>
                    {metrics.map(metric => (
                        <div key={metric.metric}>{parseInt(metric.score)}</div>
                    ))}
                </div>
            ),
        },
    ];

    const dataSource = scenarioResults.map(result => ({
        ticker: result.ticker,
        company: result.company,
        total_score: result.total_score,
        fundamental_score: result.fundamental_score,
        concentration_pct: result.concentration_pct,
        concentration_bonus: result.concentration_bonus,
        unrealized_pl_eur: result.unrealized_pl_eur,
        unrealized_pl_pct: result.unrealized_pl_pct,
        total_amount_exposure: result.total_amount_exposure,
        metrics: result.metrics,
    }));

    return (
        <div style={{ display: 'flex' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginRight: '20px' }}>
                {currentRegime && (
                    <div style={{ marginBottom: '12px', fontSize: '12px' }}>
                        Current market regime: <Tag color={currentRegime.toUpperCase() === 'BEAR' ? 'red' : currentRegime.toUpperCase() === 'BULL' ? 'green' : 'gold'}>{currentRegime}</Tag>
                        {suggestedScenario && <div style={{ color: '#888' }}>Suggested: {suggestedScenario}</div>}
                    </div>
                )}
                {scenarios.map(scenario => (
                    <Button
                        key={scenario}
                        type={selectedScenario === scenario ? 'primary' : 'default'}
                        style={{ marginBottom: '10px', backgroundColor: selectedScenario === scenario ? 'red' : undefined }}
                        onClick={() => handleScenarioSelect(scenario)}
                    >
                        {scenario}
                        {scenario === suggestedScenario && (
                            <Tag color="blue" style={{ marginLeft: '8px' }}>Suggested</Tag>
                        )}
                    </Button>
                ))}
            </div>
            <div style={{ flex: 1 }}>
                {selectedScenario && <h3>Selected Scenario: {selectedScenario}</h3>}
                {selectedScenario && (
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                        Medium-term (~6-month) signal from current fundamentals vs sector averages — not a short-term trading call.
                        Holdings with no genuine sell signal are omitted (the list may show fewer than 3).
                    </div>
                )}
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="ticker"
                    pagination={false}
                />

                {selectedScenario && (
                    <div style={{ marginTop: '16px' }}>
                        <Button type="default" loading={llmLoading} onClick={handleAiAnalysis}>
                            AI analysis
                        </Button>
                        {llmAnalysis && (
                            <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #eee', borderRadius: '6px' }}>
                                <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px' }}>
                                    AI curation ({llmAnalysis.model || 'n/a'}) · regime {llmAnalysis.regime} · {llmAnalysis.candidate_count} candidates
                                    {llmAnalysis.cost_usd ? ` · ~$${Number(llmAnalysis.cost_usd).toFixed(3)}` : ''}
                                </div>
                                {llmAnalysis.analysis?.summary && (
                                    <p style={{ marginBottom: '8px' }}>{llmAnalysis.analysis.summary}</p>
                                )}
                                {(llmAnalysis.analysis?.recommended_actions || []).map((a, i) => (
                                    <div key={i} style={{ marginBottom: '6px' }}>
                                        <Tag color={a.action === 'sell' ? 'red' : a.action === 'trim' ? 'orange' : 'default'}>
                                            {(a.action || '').toUpperCase()}
                                        </Tag>
                                        <strong>{a.ticker}</strong> {a.company ? `(${a.company})` : ''} — {a.rationale}
                                        {a.caution ? <span style={{ color: '#cf1322' }}> ⚠ {a.caution}</span> : ''}
                                    </div>
                                ))}
                                {llmAnalysis.error && (
                                    <div style={{ color: '#cf1322', fontSize: '12px' }}>Note: {llmAnalysis.error}</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {etfCandidates.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                        <h3>ETFs &amp; funds</h3>
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                            No fundamentals are available for funds — ranked on size (share of your fund sleeve)
                            and recent performance only. A review aid, not the fundamental scenario score.
                        </div>
                        <Table
                            dataSource={etfCandidates.map(c => ({ ...c, key: c.ticker }))}
                            pagination={false}
                            columns={[
                                {
                                    title: 'Fund', dataIndex: 'ticker', key: 'ticker',
                                    render: (t, r) => (<div><Tag color="purple">{t}</Tag><div style={{ fontSize: '12px' }}>{r.company}</div></div>),
                                },
                                { title: '% of fund sleeve', dataIndex: 'sleeve_concentration_pct', key: 'conc', render: v => `${v}%` },
                                { title: '52-wk perf', dataIndex: 'week_52_perf', key: 'perf', render: v => (v == null ? '—' : `${v}%`) },
                                {
                                    title: 'Unrealized P/L', dataIndex: 'unrealized_pl_pct', key: 'pl',
                                    render: v => (v == null ? '—' : <span style={{ color: v < 0 ? '#cf1322' : '#389e0d' }}>{v}%</span>),
                                },
                            ]}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default RebalanceScenarios;
